// @ts-nocheck
import React, { useState, useEffect, useContext, useMemo } from "react";
import { HotelContext } from "../../context/HotelContext";
import { formatSummaryStatDelta } from "../../components/v2/cards/SummaryStatCard";
import { getLocale, translate } from "../../utils/Translation";
import { getSinceInstallationLabel } from "../../utils/installationDate";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import Translation from "utils/Translation";
import {
  ActiveStaysCard,
  DeclinedServicesCard,
  DashboardSummaryCardsRow,
  EconomiesTrendCard,
  RecentReviewsCard,
  TopActionsCard,
} from "./DashboardCards";
import "./Page.css";
import "./Dashboard.css";
import bedIcon from "../../img/v2/bed.png";
import CleanLogo from "../../img/v2/clean-logo.svg";
import LeafIcon from "../../img/v2/leaf-icon.svg";
import WaterIcon from "../../img/v2/water-icon.svg";
import { useV2Year } from "../../hooks/useV2Year";
import { formatDashboardChartData } from "../../utils/formatDashboardChartData";
import {
  getDemoHotel73DashboardStats,
  getDemoHotel73WaterSummary,
  isDemoHotel73,
} from "../../utils/demoHotel73Stats";

// Refonte plateforme : masquer temporairement les cards du bas (services, séjours, top actions, avis).
// Remettre à true pour réafficher sans toucher au JSX ni aux fetchers.
const SHOW_DASHBOARD_BOTTOM_SECTION = false;

const DashboardV2 = ({ selectedHotelId, onHotelChange }) => {
  const keycloak = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);

  const { year: selectedYear, setYear } = useV2Year();

  const [chartData, setChartData] = useState([]);
  const [chartDataRaw, setChartDataRaw] = useState([]);
  const [platformInstallationMissing, setPlatformInstallationMissing] = useState(false);
  const [installationDate, setInstallationDate] = useState(null);
  const [stats, setStats] = useState({
    numberOfShowers: 0,
    totalWaterSaved: 0,
    totalMoneySaved: 0,
    averageReductionPercentage: 0,
  });
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedPeriodType, setSelectedPeriodType] = useState("beginning");
  const [activeButton, setActiveButton] = useState("beginning");
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [waterSummary, setWaterSummary] = useState({
    value: null,
    variation: null,
  });
  const [waterSummaryLoading, setWaterSummaryLoading] = useState(false);
  const [servicesDeclined, setServicesDeclined] = useState([]);
  const [servicesDeclinedLoading, setServicesDeclinedLoading] = useState(false);
  const [recentActions, setRecentActions] = useState([]);
  const [recentActionsLoading, setRecentActionsLoading] = useState(false);
  const [topActions, setTopActions] = useState([]);
  const [topActionsLoading, setTopActionsLoading] = useState(false);
  const [recentReviews, setRecentReviews] = useState([]);
  const [recentReviewsLoading, setRecentReviewsLoading] = useState(false);
  const [hotelFeatures, setHotelFeatures] = useState([]);
  const [featuresHotelId, setFeaturesHotelId] = useState(null);
  const hasWaterOption = featuresHotelId === selectedHotelId && hotelFeatures.includes("Option Eau");

  const fetchHotelSubscriptions = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      setHotelFeatures([]);
      setFeaturesHotelId(null);
      return;
    }

    fetch(`/api/hotels/${selectedHotelId}/subscriptions/`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const features = (data.subscription || []).map((item) => item.fName);
        setHotelFeatures(features);
        setFeaturesHotelId(selectedHotelId);
      })
      .catch((error) => {
        console.error("Error fetching hotel subscriptions:", error);
        setHotelFeatures([]);
        setFeaturesHotelId(null);
      });
  };

  const fetchHotels = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token) {
      return;
    }

    setHotelsLoading(true);
    fetch("/api/groups/hotels", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === "OK") {
          const independentHotels = (data.independent || []).map((hotel) => ({
            id: hotel.hId,
            name: hotel.name || hotel.hName || `Hôtel ${hotel.hId}`,
          }));

          const groupHotels = (data.groups || []).flatMap((group) =>
            (group.hotels || []).map((hotel) => ({
              id: hotel.hId || hotel.id,
              name: hotel.name || hotel.hName || `Hôtel ${hotel.hId || hotel.id}`,
            }))
          );

          const allHotels = [...independentHotels, ...groupHotels];
          setHotels(allHotels);
          if (allHotels.length > 0 && !selectedHotelId) {
            onHotelChange(allHotels[0].id);
          }
        }
        setHotelsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching hotels:", err);
        setHotelsLoading(false);
      });
  };

  const fetchStatsByHotel = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setNoDataAvailable(false);

    if (isDemoHotel73(selectedHotelId)) {
      const data = getDemoHotel73DashboardStats();
      setStats(data.stats);
      setChartDataRaw(data.chartData || []);
      setPlatformInstallationMissing(false);
      setInstallationDate(data.installationDate || null);
      setHasError(false);
      setNoDataAvailable(false);
      setLoading(false);
      return;
    }

    fetch(
      `/api/hotels/${selectedHotelId}/stats?periodType=${selectedPeriodType}`,
      {
      method: "get",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success === "OK") {
          console.log("[Dashboard] installationDate:", data.installationDate, "| platformInstallationMissing:", data.platformInstallationMissing);
          console.table((data.chartData || []).map(({ month, hasMonthlyRecord, showOnPlatform, isPlatformMonthPending, totalLuniShowerSavedInEuros, totalActionsSavedInEuros }) => ({ month, hasMonthlyRecord, showOnPlatform, isPlatformMonthPending, totalLuniShowerSavedInEuros, totalActionsSavedInEuros })));
          setStats(data.stats);
          const rawData = data.chartData || [];
          setChartDataRaw(rawData);
          setPlatformInstallationMissing(Boolean(data.platformInstallationMissing));
          setInstallationDate(data.installationDate || null);
          setHasError(false);
        } else if (data.success === "FAIL" && data.message === "No data found for graph.") {
          setNoDataAvailable(true);
          setHasError(false);
          setChartData([]);
          setChartDataRaw([]);
          setPlatformInstallationMissing(false);
          setInstallationDate(null);
          setStats({
            numberOfShowers: 0,
            totalWaterSaved: 0,
            totalMoneySaved: 0,
            totalGreenMilesStays: 0,
            greenMilesStaysVariation: null,
            financialSavingsVariation: null,
            waterSavedVariation: null,
            averageReductionPercentage: 0,
          });
        } else {
          setHasError(true);
          setInstallationDate(null);
          console.error("Error fetching stats:", data.message);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setHasError(true);
        setInstallationDate(null);
        setLoading(false);
      });
  };

  const fetchWaterSummaryByHotel = () => {
    if (!hasWaterOption || !keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      setWaterSummary({ value: null, variation: null });
      setWaterSummaryLoading(false);
      return;
    }

    setWaterSummaryLoading(true);

    if (isDemoHotel73(selectedHotelId)) {
      const summary = getDemoHotel73WaterSummary();
      setWaterSummary({
        value: summary.value,
        variation: summary.variation,
      });
      setWaterSummaryLoading(false);
      return;
    }

    const params = new URLSearchParams({ lang });

    fetch(`/api/hotels/${selectedHotelId}/water/stats?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error (water): ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setWaterSummary({
          value: data?.waterSaved?.value ?? null,
          variation: data?.waterSaved?.variation ?? null,
        });
        setWaterSummaryLoading(false);
      })
      .catch((error) => {
        console.error("Dashboard - Error fetching water summary:", error);
        setWaterSummary({ value: null, variation: null });
        setWaterSummaryLoading(false);
      });
  };

  const fetchRecentDeclinedServices = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      return;
    }

    setServicesDeclinedLoading(true);
    fetch(
      `/api/hotels/${selectedHotelId}/declined-services/recent?limit=3&lang=${lang}`,
      {
        method: "GET",
        headers: {
          Authorization: "Bearer " + keycloak.user.access_token,
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-front-token": "Bs#PSQyhy)!y9Ti{",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success === "OK" && Array.isArray(data.rooms)) {
          // Mapper les données backend vers le format attendu par le frontend
          const mapped = data.rooms.map((room) => {
            // Même mapping que "Statistiques d'utilisation / Tendances d'usage"
            const iconSrc = iconForActType(room.actType);

            // Formater le texte de l'action
            let actionText = room.actNameTranslated || room.actName || "";
            // Si le texte ne commence pas par "Refus", on peut l'ajouter (optionnel)
            // Pour l'instant, on utilise directement la traduction du backend

            return {
              room: room.roomNumber || "",
              iconSrc: iconSrc,
              action: actionText,
            };
          });
          setServicesDeclined(mapped.slice(0, 3));
        } else {
          setServicesDeclined([]);
        }
        setServicesDeclinedLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching declined services:", error);
        setServicesDeclined([]);
        setServicesDeclinedLoading(false);
      });
  };

  const iconForActType = (actType) => {
    const t = String(actType || "").toUpperCase();
    // Même mapping que "Statistiques d'utilisation / Tendances d'usage" (ActionsList v2)
    if (t === "LUNICLEAN") return CleanLogo;
    if (t === "LUNISHOWER") return WaterIcon;
    return LeafIcon;
  };

  const fetchRecentActions = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      return;
    }

    setRecentActionsLoading(true);
    fetch(`/api/hotels/${selectedHotelId}/actions/recent?limit=3&lang=${lang}&today=1`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success === "OK" && Array.isArray(data.actions)) {
          const mapped = data.actions.map((a) => ({
            room: a.roomNumber || "",
            iconSrc: iconForActType(a.actType),
            action: a.actNameTranslated || a.actName || a.actType || "",
          }));
          setRecentActions(mapped.slice(0, 3));
        } else {
          setRecentActions([]);
        }
        setRecentActionsLoading(false);
      })
      .catch(() => {
        setRecentActions([]);
        setRecentActionsLoading(false);
      });
  };

  const fetchTopActions = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      return;
    }

    setTopActionsLoading(true);
    fetch(
      `/api/hotels/${selectedHotelId}/actions/top?limit=3&lang=${lang}&year=${selectedYear}`,
      {
        method: "GET",
        headers: {
          Authorization: "Bearer " + keycloak.user.access_token,
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-front-token": "Bs#PSQyhy)!y9Ti{",
        },
      }
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.success === "OK" && Array.isArray(data.top)) {
          setTopActions(data.top.slice(0, 3));
        } else {
          setTopActions([]);
        }
        setTopActionsLoading(false);
      })
      .catch(() => {
        setTopActions([]);
        setTopActionsLoading(false);
      });
  };

  const fetchRecentReviews = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !selectedHotelId) {
      return;
    }

    setRecentReviewsLoading(true);
    fetch(`/api/hotels/${selectedHotelId}/reviews/recent?limit=2`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success === "OK" && Array.isArray(data.reviews)) {
          const mapped = data.reviews.map((rev) => ({
            id: rev.id,
            author: rev.author_name || "—",
            text: rev.review_text || "",
            createdAt: rev.created_at,
            rating: rev.rating,
            platform: rev.platform,
          }));
          setRecentReviews(mapped.slice(0, 2));
        } else {
          setRecentReviews([]);
        }
        setRecentReviewsLoading(false);
      })
      .catch(() => {
        setRecentReviews([]);
        setRecentReviewsLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user) {
      fetchHotels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated]);

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user && selectedHotelId) {
      fetchHotelSubscriptions();
    } else {
      setHotelFeatures([]);
      setFeaturesHotelId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated, selectedHotelId]);

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user && selectedHotelId) {
      fetchStatsByHotel();
      if (SHOW_DASHBOARD_BOTTOM_SECTION) {
        fetchRecentDeclinedServices();
        fetchRecentActions();
        fetchTopActions();
        fetchRecentReviews();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated, selectedHotelId, selectedPeriodType, lang]);

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user && selectedHotelId) {
      fetchWaterSummaryByHotel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated, selectedHotelId, lang, hasWaterOption]);

  useEffect(() => {
    const formatted = formatDashboardChartData(chartDataRaw, selectedYear, null, lang, new Date(), {
      platformInstallationMissing,
      hasWaterOption,
    });
    console.log("[Dashboard] formatDashboardChartData year=%s | platformInstallationMissing=%s", selectedYear, platformInstallationMissing);
    console.table(formatted.map(({ month, hasMonthlyRecord, showOnPlatform, isPlatformMonthPending, isFutureMonth, totalLuniShowerSavedInEuros, totalActionsSavedInEuros }) => ({ month, hasMonthlyRecord, showOnPlatform, isPlatformMonthPending, isFutureMonth, totalLuniShowerSavedInEuros, totalActionsSavedInEuros })));
    setChartData(
      formatted
    );
  }, [chartDataRaw, selectedYear, lang, platformInstallationMissing, hasWaterOption]);

  // Header simplifié (comme la maquette) : le choix d'hôtel est géré ailleurs (navigation),
  // et la période est fixée sur "Depuis le début".

  // Variations : mois calendaire en cours vs mois précédent (calculées côté API)
  const moneyDelta = stats.financialSavingsVariation;
  const waterDelta = waterSummary.variation;
  const gmStaysDelta = stats.greenMilesStaysVariation;

  const hasV2GreenMiles = hotelFeatures.includes("V2-GreenMiles");
  const summarySubtitle = getSinceInstallationLabel(installationDate, lang, locale);
  const summaryCards = useMemo(
    () => [
      {
        id: "financial",
        title: translate("v2_dashboard_financial_savings", lang),
        subtitle: summarySubtitle,
        value: noDataAvailable ? "..." : `${Math.round(stats.totalMoneySaved || 0).toLocaleString(locale)}`,
        valueUnit: "€",
        deltaValue: noDataAvailable ? "..." : formatSummaryStatDelta(moneyDelta, "€", locale),
        accentColor: "#FAA54B",
      },
      {
        id: "water",
        title: translate("v2_dashboard_water_savings", lang),
        subtitle: summarySubtitle,
        value: waterSummary.value === null ? null : `${Math.round(waterSummary.value || 0).toLocaleString(locale)}`,
        valueUnit: "L",
        deltaValue: formatSummaryStatDelta(waterDelta, " L", locale),
        accentColor: "#5B97EE",
        loading: waterSummaryLoading,
        hide: !hasWaterOption,
      },
      {
        id: "greenmiles-users",
        title: translate("v2_dashboard_greenmiles_users", lang),
        subtitle: summarySubtitle,
        value: noDataAvailable ? "..." : `${Math.round(stats.totalGreenMilesStays || 0).toLocaleString(locale)}`,
        deltaValue: noDataAvailable ? "..." : formatSummaryStatDelta(gmStaysDelta, "", locale),
        accentColor: "#3C5544",
        hide: !hasV2GreenMiles,
      },
    ],
    [
      lang,
      locale,
      noDataAvailable,
      summarySubtitle,
      stats.totalMoneySaved,
      waterSummary.value,
      stats.totalGreenMilesStays,
      moneyDelta,
      waterDelta,
      gmStaysDelta,
      waterSummaryLoading,
      hasV2GreenMiles,
      hasWaterOption,
    ]
  );

  return (
    <div className="v2-page dashboard-v2">
      <div className="v2-page__header">
        <h1>
          <Translation tag={"Dashboard_title"} />
        </h1>
      </div>

      <div className="v2-page__content">
        <DashboardSummaryCardsRow cards={summaryCards} />

        <EconomiesTrendCard
          loading={loading}
          hasError={hasError}
          noDataAvailable={noDataAvailable}
          chartData={chartData}
          selectedYear={selectedYear}
          onPreviousYear={() => setYear(selectedYear - 1)}
          onNextYear={() => setYear(selectedYear + 1)}
          hasWaterOption={hasWaterOption}
        />

      {/* Bloc bottom comme maquette (services, séjours, top actions, avis) — désactivé via SHOW_DASHBOARD_BOTTOM_SECTION */}
      {SHOW_DASHBOARD_BOTTOM_SECTION && (
      <div className="dashboard-v2__bottom">
        <DeclinedServicesCard
          servicesDeclinedLoading={servicesDeclinedLoading}
          servicesDeclined={servicesDeclined}
          bedIcon={bedIcon}
          onOpenAllRooms={() => {
            navigate(`/hotel/lunishower/v2/actions-cleaning${location.search}`);
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: "instant" });
            }, 100);
          }}
        />

        <ActiveStaysCard
          recentActionsLoading={recentActionsLoading}
          recentActions={recentActions}
          bedIcon={bedIcon}
        />

        <TopActionsCard
          topActionsLoading={topActionsLoading}
          topActions={topActions}
          iconForActType={iconForActType}
        />

        <RecentReviewsCard
          recentReviewsLoading={recentReviewsLoading}
          recentReviews={recentReviews}
        />
      </div>
      )}
      </div>
    </div>
  );
};

export default DashboardV2;
